import {
  PencilSquareIcon,
  PlusIcon,
  TrashIcon,
  TagIcon,
} from '@heroicons/react/24/outline';
import {
  useCreateLabelMutation,
  useDeleteLabelMutation,
  useEditLabelMutation,
} from '@youshift/shared/hooks/mutations/chains';
import { slotLabelsQuery } from '@youshift/shared/hooks/queries';
import { SlotLabel } from '@youshift/shared/types';
import {
  ColorIndex,
  ColorName,
  LabelIcon,
  colorIndices,
  colorNames,
  getRandomColor,
  returnColor,
} from '@youshift/shared/utils';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLoaderData, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { QueryClient, useQuery, useQueryClient } from '@tanstack/react-query';
import { preLoadQuery } from '@youshift/shared/hooks';

import { YSButton } from '../../../components/Buttons';
import IconPicker from '../../../components/IconPicker';
import LabelIconComponent from '../../../components/LabelIconComponent';
import Modal from '../../../components/Modal';
import ShadePicker from '../../../components/ShadePicker';
import Wrapper from '../../../components/Wrapper';
import ColorPicker from '../../../components/ColorPicker';
import DeleteConfirmationModal from '../../../components/DeleteConfirmationModal';
import { EmptyState } from '../../../components/EmptyState';
import { LabelBadge } from '../../../components/Calendars/LabelBadge';

// Create the loader function
export const labelControlCenterLoader =
  (queryClient: QueryClient) =>
  async ({
    params,
  }: {
    params: { idChain: string };
  }): Promise<Record<number, SlotLabel>> => {
    const slotLabels = await preLoadQuery(
      queryClient,
      slotLabelsQuery(Number(params.idChain)),
    );
    return slotLabels;
  };

export default function LabelControlCenter() {
  const { idChain } = useParams();
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const slotLabelsLoaderData = useLoaderData() as Record<number, SlotLabel>;

  const { data: labels, isLoading } = useQuery({
    ...slotLabelsQuery(Number(idChain)),
    enabled: !!idChain,
    initialData: slotLabelsLoaderData,
  });

  // State for modals
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  // State for label form
  const [labelName, setLabelName] = useState('');
  const [labelColor, setLabelColor] = useState(() => getRandomColor());
  const [labelIcon, setLabelIcon] = useState<LabelIcon | null>(null);
  const [selectedLabel, setSelectedLabel] = useState<SlotLabel | null>(null);

  const resetForm = () => {
    setLabelName('');
    setLabelColor(getRandomColor());
    setLabelIcon(null);
    setSelectedLabel(null);
  };

  // Mutations
  const createLabelMutation = useCreateLabelMutation(queryClient, {
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['slotLabels', Number(idChain)],
      });
      toast.success(t('manager.labels.createLabelSuccess'));
      resetForm();
      setCreateModalOpen(false);
    },
  });

  const editLabelMutation = useEditLabelMutation(queryClient, {
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['slotLabels', Number(idChain)],
      });
      toast.success(t('manager.labels.editLabelSuccess'));
      resetForm();
      setEditModalOpen(false);
    },
  });

  const deleteLabelMutation = useDeleteLabelMutation(queryClient, {
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['slotLabels', Number(idChain)],
      });
      toast.success(t('manager.labels.eliminateLabelSuccess'));
      setDeleteModalOpen(false);
      setSelectedLabel(null);
    },
  });

  // Helper functions
  const handleCreateLabel = () => {
    if (!labelName.trim() || !labelIcon) return;

    createLabelMutation.mutate({
      id_chain: Number(idChain),
      name: labelName,
      color: labelColor,
      icon: labelIcon,
    });
  };

  const handleEditLabel = () => {
    if (!selectedLabel || !labelName.trim() || !labelIcon) return;

    editLabelMutation.mutate({
      id_chain: Number(idChain),
      id_slot_label: selectedLabel.id_slot_label,
      name: labelName,
      color: labelColor,
      icon: labelIcon,
    });
  };

  const handleDeleteLabel = () => {
    if (!selectedLabel) return;

    deleteLabelMutation.mutate({
      id_chain: Number(idChain),
      id_slot_label: selectedLabel.id_slot_label,
    });
  };

  const openEditModal = (label: SlotLabel) => {
    setSelectedLabel(label);
    setLabelName(label.name);
    setLabelColor(label.color);
    setLabelIcon(label.icon);
    setEditModalOpen(true);
  };

  const openDeleteModal = (label: SlotLabel) => {
    setSelectedLabel(label);
    setDeleteModalOpen(true);
  };

  return (
    <Wrapper>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">{t('manager.labels.title')}</h1>
        <YSButton
          variant="primary"
          onClick={() => setCreateModalOpen(true)}
          classNames="flex items-center gap-2"
        >
          <PlusIcon className="w-5 h-5" />
          {t('manager.labels.createLabel')}
        </YSButton>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      ) : labels && Object.values(labels).length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {Object.values(labels)
            .sort((a, b) => a.name.localeCompare(b.name))
            .map(label => (
              <div
                key={label.id_slot_label}
                className="border rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <LabelBadge icon={label.icon} labelColor={label.color} />
                    <h3 className="font-medium text-lg">{label.name}</h3>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => openEditModal(label)}
                      className="p-1 rounded-full hover:bg-gray-100"
                      aria-label={t('generic.edit')}
                    >
                      <PencilSquareIcon className="w-5 h-5 text-gray-600" />
                    </button>
                    <button
                      onClick={() => openDeleteModal(label)}
                      className="p-1 rounded-full hover:bg-gray-100"
                      aria-label={t('generic.delete')}
                    >
                      <TrashIcon className="w-5 h-5 text-red-600" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
        </div>
      ) : (
        <EmptyState
          Icon={TagIcon}
          title={t('manager.labels.noLabelsTitle')}
          subtitle={t('manager.labels.noLabelsSubtitle')}
          buttonText={t('manager.labels.createFirstLabel')}
          onClick={() => setCreateModalOpen(true)}
        />
      )}

      {/* Create Label Modal */}
      <Modal
        isOpen={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        size="md"
        editButtons
        // title={t('manager.labels.createLabel')}
        handleSave={handleCreateLabel}
        loadingSave={createLabelMutation.isPending}
        disableSave={!labelName.trim() || !labelIcon}
      >
        <div className="space-y-4">
          <div>
            <label
              htmlFor="labelName"
              className="block text-sm font-medium text-gray-700"
            >
              {t('generic.name')}
            </label>
            <input
              type="text"
              id="labelName"
              value={labelName}
              onChange={e => setLabelName(e.target.value)}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('generic.color')}
            </label>
            <ColorPicker value={labelColor} onChange={setLabelColor} />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('generic.icon')}
            </label>
            <IconPicker value={labelIcon} onChange={setLabelIcon} />
          </div>

          {labelName && labelColor && (
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('generic.preview')}
              </label>
              <div className="flex items-center gap-2 p-3 border rounded-md">
                {labelIcon && (
                  <LabelBadge icon={labelIcon} labelColor={labelColor} />
                )}
                <span className="font-medium">{labelName}</span>
              </div>
            </div>
          )}
        </div>
      </Modal>

      {/* Edit Label Modal */}
      <Modal
        isOpen={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        size="md"
        editButtons
        // title={t('manager.labels.editLabel')}
        handleSave={handleEditLabel}
        loadingSave={editLabelMutation.isPending}
        disableSave={!labelName.trim() || !labelIcon}
      >
        <div className="space-y-4">
          <div>
            <label
              htmlFor="editLabelName"
              className="block text-sm font-medium text-gray-700"
            >
              {t('generic.name')}
            </label>
            <input
              type="text"
              id="editLabelName"
              value={labelName}
              onChange={e => setLabelName(e.target.value)}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('generic.color')}
            </label>
            <ColorPicker value={labelColor} onChange={setLabelColor} />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('generic.icon')}
            </label>
            <IconPicker value={labelIcon} onChange={setLabelIcon} />
          </div>

          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('generic.preview')}
            </label>
            <div className="flex items-center gap-2 p-3 border rounded-md">
              {labelIcon && (
                <LabelBadge icon={labelIcon} labelColor={labelColor} />
              )}
              <span className="font-medium">{labelName}</span>
            </div>
          </div>
        </div>
      </Modal>

      {/* Delete Label Modal */}
      <DeleteConfirmationModal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={handleDeleteLabel}
        title={t('manager.labels.deleteLabel')}
        description={t('manager.labels.deleteWarning', {
          labelName: selectedLabel?.name,
        })}
        confirmationText={t('generic.delete')}
      />
    </Wrapper>
  );
}
